"use client";

import { Link, useLocation } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  CarFront,
  LayoutDashboard,
  LifeBuoy,
  Menu,
  Users,
} from "lucide-react";
import { cn } from "../utils/cn";
import { useEffectiveAccess } from "../hooks/useEffectiveAccess";
import type { ComponentType } from "react";

interface MobileBottomNavProps {
  appScope: "business" | "platform-admin";
  onMoreClick: () => void;
}

interface NavTab {
  label: string;
  icon: ComponentType<{ className?: string }>;
  href: string;
  module?: string; // If set, only shown when plan includes this module
}

const businessTabs: NavTab[] = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/" },
  { label: "Bookings", icon: CalendarDays, href: "/bookings", module: "bookings" },
  { label: "Vehicles", icon: CarFront, href: "/vehicles", module: "vehicles" },
  { label: "Customers", icon: Users, href: "/customers", module: "customers" },
];

const platformAdminTabs: NavTab[] = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/console" },
  { label: "Businesses", icon: Building2, href: "/console/businesses" },
  { label: "Support", icon: LifeBuoy, href: "/console/support" },
];

export function MobileBottomNav({ appScope, onMoreClick }: MobileBottomNavProps) {
  const { pathname } = useLocation();
  const { canAccess } = useEffectiveAccess();
  const allTabs = appScope === "platform-admin" ? platformAdminTabs : businessTabs;

  // Filter tabs by plan access (only for business app)
  const tabs = appScope === "business"
    ? allTabs.filter((tab) => !tab.module || canAccess(tab.module))
    : allTabs;

  const isTabActive = (href: string) => {
    if (href === "/" || href === "/console") {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around">
        {tabs.map((tab) => {
          const active = isTabActive(tab.href);
          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={cn(
                "flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs font-medium transition-colors",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-current={active ? "page" : undefined}
            >
              <tab.icon className={cn("h-5 w-5", active && "text-primary")} />
              <span>{tab.label}</span>
            </Link>
          );
        })}

        {/* More button — opens sidebar for additional navigation */}
        <button
          type="button"
          onClick={onMoreClick}
          className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}
